import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { X, Clock, CheckCircle, CheckSquare, MessageCircle, FileText, Plus, Sparkles, Maximize2, Activity, GitBranch, Upload, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';
import { useSubscription } from '../utils/useSubscription';
import AIDraftGenerator from './AIDraftGenerator';
import ChatMessages from './ChatMessages';
import ActivityTimeline from './ActivityTimeline';
import StatusChanger from './StatusChanger';
import AITimeSuggestion from './AITimeSuggestion';
import AITaskSuggestions from './AITaskSuggestions';
import PhaseManager from './PhaseManager';
import DocumentManager from './DocumentManager';
import PaywallModal from './PaywallModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CasePanel = ({ caseId, onClose }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  const user = auth.getUser();
  const { subscription, canAccess, isPro } = useSubscription();
  const [activeTab, setActiveTab] = useState('chat');
  const [caseData, setCaseData] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);

  // Time form
  const [timeForm, setTimeForm] = useState({
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Task form
  const [taskForm, setTaskForm] = useState({
    title: '',
    due_date: ''
  });

  useEffect(() => {
    console.log('CasePanel mounted with caseId:', caseId);
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    console.log('Fetching case data for:', caseId);
    try {
      const [caseRes, workspaceRes, docsRes] = await Promise.all([
        axios.get(`${API}/cases/${caseId}`),
        axios.get(`${API}/lexy/workspace/${caseId}`).catch(() => ({ data: null })),
        axios.get(`${API}/documents/${caseId}`).catch(() => ({ data: { documents: [] } }))
      ]);
      
      console.log('Case data:', caseRes.data);
      console.log('Workspace data:', workspaceRes.data);
      console.log('Documents:', docsRes.data);
      
      setCaseData(caseRes.data);
      setDocuments(docsRes.data.documents || []);
      
      // Safe access to workspace data
      const workspaceData = workspaceRes?.data || {};
      setTimeEntries(workspaceData.time?.entries || []);
      setTasks(workspaceData.tasks?.tasks || []);
    } catch (error) {
      console.error('Error loading case:', error);
      toast.error(t('Could not load case', 'Kunne ikke laste sak'));
    } finally {
      setLoading(false);
    }
  };

  const addTime = async (e) => {
    e.preventDefault();
    
    // Check Pro access for Lexy cases
    if (caseData?.source === 'LEXY' && !canAccess('time_tracking')) {
      setPaywallFeature('time_tracking');
      setShowPaywall(true);
      return;
    }
    
    try {
      await axios.post(`${API}/lexy/time-entries`, {
        case_id: caseId,
        user_id: user.id,
        hours: parseFloat(timeForm.hours),
        description: timeForm.description,
        date: timeForm.date,
        billable: true,
        hourly_rate: 1500
      });
      
      toast.success(t('Time registered!', 'Time registrert!'));
      setTimeForm({ hours: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchCaseData();
    } catch (error) {
      toast.error(t('Error', 'Feil'));
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    
    // Check Pro access
    if (caseData?.source === 'LEXY' && !canAccess('tasks')) {
      setPaywallFeature('tasks');
      setShowPaywall(true);
      return;
    }
    
    try {
      await axios.post(`${API}/lexy/tasks`, {
        case_id: caseId,
        created_by: user.id,
        title: taskForm.title,
        due_date: taskForm.due_date,
        priority: 'MEDIUM'
      });
      
      toast.success(t('Task created!', 'Oppgave opprettet!'));
      setTaskForm({ title: '', due_date: '' });
      fetchCaseData();
    } catch (error) {
      toast.error(t('Error', 'Feil'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header - Linear minimal */}
      <div className="h-14 px-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h2 className="font-semibold text-gray-900 text-base truncate">{caseData?.topic || caseData?.title}</h2>
          {caseData?.source && (
            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
              caseData.source === 'MARKETPLACE' 
                ? 'bg-gray-100 text-gray-800' 
                : 'bg-gray-100 text-gray-700'
            }`}>
              {caseData.source === 'MARKETPLACE' ? 'Marketplace' : 'Lexy'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/lexy/case/${caseId}/workspace`)}
            className="px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-700 text-sm font-medium flex items-center gap-1.5"
          >
            <Maximize2 className="w-4 h-4" />
            Fullskjerm
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Tabs - Linear style */}
      <div className="h-12 px-5 border-b border-gray-200 flex items-center gap-1 overflow-x-auto flex-shrink-0">
        {caseData?.source === 'MARKETPLACE' ? (
          <>
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'chat'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              Chat
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'activity'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Activity', 'Aktivitet')}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'chat'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              Chat
            </button>
            <button
              onClick={() => setActiveTab('phases')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'phases'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Phases', 'Faser')}
            </button>
            <button
              onClick={() => setActiveTab('time')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'time'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Time', 'Timer')}
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'tasks'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Tasks', 'Oppgaver')}
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'documents'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Documents', 'Dokumenter')}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'activity'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('Activity', 'Aktivitet')}
            </button>
          </>
        )}
      </div>

      {/* Tab Content - Linear style */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'chat' && (
          <ChatMessages caseId={caseId} />
        )}

        {activeTab === 'draft' && caseData?.source === 'MARKETPLACE' && (
          <AIDraftGenerator 
            caseData={caseData} 
            onAnswerSent={() => {
              toast.success('Svar sendt!');
              onClose();
            }}
          />
        )}

        {activeTab === 'phases' && caseData?.source === 'LEXY' && (
          <div className="p-6">
            <PhaseManager caseId={caseId} caseData={caseData} />
          </div>
        )}

        {activeTab === 'time' && (
          <div className="p-6 space-y-6">
            {/* AI Time Suggestion */}
            <AITimeSuggestion 
              caseId={caseId} 
              onTimeAdded={() => {
                fetchCaseData();
              }}
            />
            
            <form onSubmit={addTime} className="bg-gray-50 rounded-xl p-4 space-y-3">
              <Input
                type="number"
                step="0.5"
                placeholder={t('Hours', 'Timer')}
                value={timeForm.hours}
                onChange={(e) => setTimeForm({...timeForm, hours: e.target.value})}
                className="h-10"
                required
              />
              <Input
                placeholder={t('Description', 'Beskrivelse')}
                value={timeForm.description}
                onChange={(e) => setTimeForm({...timeForm, description: e.target.value})}
                className="h-10"
                required
              />
              <Button type="submit" size="sm" className="w-full bg-gray-900 hover:bg-gray-800">
                + {t('Add', 'Legg til')}
              </Button>
            </form>
            
            <div className="space-y-2">
              {timeEntries.map((entry) => (
                <div key={entry.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">{entry.description}</span>
                    <span className="font-bold">{entry.hours}t</span>
                  </div>
                  <span className="text-xs text-gray-500">{entry.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="p-6 space-y-6">
            <form onSubmit={addTask} className="bg-gray-50 rounded-xl p-4 space-y-3">
              <Input
                placeholder={t('Task', 'Oppgave')}
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                className="h-10"
                required
              />
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                className="h-10"
              />
              <Button type="submit" size="sm" className="w-full bg-gray-900 hover:bg-gray-800">
                + {t('Add', 'Legg til')}
              </Button>
            </form>
            
            <div className="space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="p-3 bg-gray-50 rounded-lg text-sm flex items-center gap-3">
                  <input type="checkbox" checked={task.completed} className="w-4 h-4" readOnly />
                  <span className={task.completed ? 'line-through text-gray-500' : 'text-gray-900'}>
                    {task.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'docs' && (
          <div className="p-6">
            <DocumentManager 
              caseId={caseId}
              caseData={caseData}
              documents={documents}
              onDocumentsChanged={fetchCaseData}
            />
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="p-6">
            <ActivityTimeline caseId={caseId} />
          </div>
        )}
      </div>
      
      {/* Paywall Modal */}
      {showPaywall && (
        <PaywallModal
          feature={paywallFeature}
          onClose={() => setShowPaywall(false)}
          onUpgraded={() => {
            setShowPaywall(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default CasePanel;
